/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { isEmpty } from 'lodash-es'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'

import styles from './goSearchBar.module.scss'
import GoSearchInput from './GoSearchInput'
import Hits from './Hits'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { RootState, useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useAllClientsData } from '../../../lyraTherapy/clients/data/hooks/useAllClientsData'
import { getIsPrivacyGuardActive } from '../../../lyraTherapy/data/ltVideoSelectors'
import { CLIENT_HOME } from '../../constants/routingConstants'
import { searchForClients } from '../../utils/utils'

export type Client = {
  first_name: string
  last_name: string
  id: string
}

/**
 * This component shows a overlayed search bar when hitting a magic key combination (CTRL + Space),
 * giving the user a typehead to search for clients and navigate to them. This is similar to the Spotlight search on Mac OS.
 */
const GoSearchBar: React.FC<ConnectedProps<typeof connector>> = ({ isPrivacyGuardActive }) => {
  const [clients] = useAllClientsData()

  const [isGoOpen, setGoOpen] = useState(false)
  const [clientHits, setClientHits] = useState<Client[]>([])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const onHitClick = (hit: Client) => {
    track({ event: 'NAVIGATE_TO_CLIENT_FROM_GO_SEARCH_BAR', properties: { client: hit } })
    dispatch(setSelectedClientId(hit.id))
    navigate(CLIENT_HOME.route)
    setClientHits([])
    setGoOpen(false)
  }

  // Close Go Search if start a video session.
  useEffect(() => {
    if (isPrivacyGuardActive) {
      setClientHits([])
      setGoOpen(false)
    }
  }, [isPrivacyGuardActive])

  useEffect(() => {
    const onMagicKeys = (event: { key: string; keyCode: number; ctrlKey: any; altKey: any }) => {
      // The magic key combination is CTRL + Space
      const isCtrlSpace = event.key === ' ' && event.ctrlKey
      const isAltSpace = event.key === ' ' && event.altKey
      if (isCtrlSpace || isAltSpace) {
        track({ event: isGoOpen ? 'CLOSE_GO_SEARCH_BAR' : 'OPEN_GO_SEARCH_BAR' })
        setClientHits([])
        setGoOpen(!isGoOpen)
      }
      if (event.key === KEYS.ESC) {
        setClientHits([])
        setGoOpen(false)
        track({ event: 'CLOSE_GO_SEARCH_BAR' })
      }
    }
    if (!isPrivacyGuardActive) {
      document.body.addEventListener('keydown', onMagicKeys)
    }

    return () => {
      document.body.removeEventListener('keydown', onMagicKeys)
    }
  }, [isGoOpen, isPrivacyGuardActive])

  if (!isGoOpen) {
    return null
  }
  return (
    <div className={styles.overlay} data-test-id='GoSearchBar'>
      <div data-test-id='GoSearchBar-search' className={styles.searchBar}>
        <div className={styles.searchIconContainer}>
          <SearchIcon fillColor={styles.x_white} size={40} />
        </div>
        <GoSearchInput onSearch={(event) => setClientHits(searchForClients({ clients, event }))} />
      </div>
      {!isEmpty(clientHits) && <Hits hits={clientHits} onHitClick={onHitClick} />}
    </div>
  )
}

type StateProps = {
  isPrivacyGuardActive: boolean
}
const mapStateToProps = (state: RootState): StateProps => ({
  // Block client search during a video session or while privacy guard is on
  isPrivacyGuardActive: getIsPrivacyGuardActive(state),
})

const connector = connect(mapStateToProps)
export default connector(GoSearchBar)
